import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, Calendar, Clock, MapPin, X, ExternalLink } from 'lucide-react';

// Calendar-based event configuration system
// Events can be easily added or removed from this array
// The carousel automatically filters to show only upcoming events
// All events are virtual and held in the ANIMAC Metaverse WhatsApp Community
const EVENTS_CONFIG = [
  {
    id: 1,
    title: "Winter Anime Con 2026",
    date: "2026-02-28",
    endDate: "2026-03-02",
    time: "10AM - 8PM",
    location: "🎙️ WhatsApp Community",
    description: "Biggest anime convention! Voice actors, panels & anime culture - all in our WhatsApp community!",
    image: "https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=1920x1080",
    category: "convention",
    registrationUrl: "https://chat.whatsapp.com/example-community"
  },
  {
    id: 2,
    title: "Animation Masterclass",
    date: "2026-03-15",
    time: "2PM - 5PM",
    location: "🎙️ WhatsApp Community",
    description: "Learn from industry pros about animation. Perfect for aspiring animators & artists!",
    image: "https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=1920x1080",
    category: "workshop",
    registrationUrl: "https://chat.whatsapp.com/example-community"
  },
  {
    id: 3,
    title: "Movie Night: Spirited Away",
    date: "2026-03-20",
    time: "7PM - 10PM",
    location: "🎙️ WhatsApp Community",
    description: "Experience Ghibli magic with fellow fans! Virtual watch party with commentary.",
    image: "https://images.unsplash.com/photo-1489599849927-2ee91cede3ba?w=1920x1080",
    category: "screening",
    registrationUrl: "https://chat.whatsapp.com/example-community"
  },
  {
    id: 4,
    title: "Cosplay Championship",
    date: "2026-04-05",
    time: "3PM - 9PM",
    location: "🎙️ WhatsApp Community",
    description: "Show off your cosplay skills! Annual championship with bigger prizes.",
    image: "https://images.unsplash.com/photo-1516450360452-9312f5e86fc7?w=1920x1080",
    category: "competition",
    registrationUrl: "https://chat.whatsapp.com/example-community"
  },
  {
    id: 5,
    title: "Manga Reading Club",
    date: "2026-04-12",
    time: "6PM - 8PM",
    location: "🎙️ WhatsApp Community",
    description: "Monthly manga gathering! Discuss latest chapters & discover new titles.",
    image: "https://images.unsplash.com/photo-1543002588-bfa74002ed7e?w=1920x1080",
    category: "community",
    registrationUrl: "https://chat.whatsapp.com/example-community"
  },
  {
    id: 6,
    title: "Spring Festival",
    date: "2026-04-20",
    time: "12PM - 10PM",
    location: "🎙️ WhatsApp Community",
    description: "Celebrate spring with anime games & performances! All fans welcome!",
    image: "https://images.unsplash.com/photo-1533174072545-7a4b6ad7a6c3?w=1920x1080",
    category: "festival",
    registrationUrl: "https://chat.whatsapp.com/example-community"
  }
];

// Helper function to get upcoming events
const getUpcomingEvents = () => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  return EVENTS_CONFIG
    .filter(event => {
      const eventDate = new Date(event.date);
      return eventDate >= today;
    })
    .sort((a, b) => new Date(a.date) - new Date(b.date));
};

// Helper function to format date - shorter format
const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric'
  });
};

// Helper function to get category color
const getCategoryColor = (category) => {
  const colors = {
    convention: 'from-purple-600 to-pink-600',
    workshop: 'from-blue-600 to-cyan-600',
    screening: 'from-yellow-600 to-orange-600',
    competition: 'from-red-600 to-orange-600',
    community: 'from-green-600 to-teal-600',
    festival: 'from-pink-600 to-rose-600'
  };
  return colors[category] || 'from-gray-600 to-slate-600';
};

const CommunityEvents = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [events, setEvents] = useState([]);
  const [isPaused, setIsPaused] = useState(false);

  useEffect(() => {
    const upcomingEvents = getUpcomingEvents();
    setEvents(upcomingEvents);
  }, []);

  // Auto-advance carousel
  useEffect(() => {
    if (events.length === 0 || isPaused || selectedEvent) return;

    const timer = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % events.length);
    }, 6000);

    return () => clearInterval(timer);
  }, [events.length, isPaused, selectedEvent]);

  const nextSlide = () => {
    setCurrentIndex((prev) => (prev + 1) % events.length);
  };

  const prevSlide = () => {
    setCurrentIndex((prev) => (prev - 1 + events.length) % events.length);
  };

  const goToSlide = (index) => {
    setCurrentIndex(index);
  };

  if (events.length === 0) {
    return null;
  }

  const currentEvent = events[currentIndex];

  return (
    <section className="relative py-10 sm:py-14 bg-black overflow-hidden">
      {/* Section Header - Mobile optimized */}
      <div className="container mx-auto px-3 sm:px-4 mb-6 sm:mb-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-xl sm:text-2xl md:text-3xl font-azonix font-bold text-white mb-1 sm:mb-2">
            <Calendar className="inline mr-2 text-purple-400" size={20} />
            Community Events
          </h2>
          <p className="text-gray-500 text-xs sm:text-sm font-inter">
            Upcoming virtual events & gatherings
          </p>
        </motion.div>
      </div>

      {/* Carousel Container - Mobile optimized heights */}
      <div 
        className="relative h-[350px] sm:h-[400px] md:h-[500px]"
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
      >
        <AnimatePresence mode="wait">
          <motion.div
            key={currentEvent.id}
            initial={{ opacity: 0, scale: 1.1 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={{ duration: 0.8 }}
            className="absolute inset-0 cursor-pointer"
            onClick={() => setSelectedEvent(currentEvent)}
          >
            {/* Event Image */}
            <div className="absolute inset-0">
              <img 
                src={currentEvent.image} 
                alt={currentEvent.title}
                className="w-full h-full object-cover"
              />
              {/* Gradient Overlays - Darker for better text contrast */}
              <div className="absolute inset-0 bg-gradient-to-r from-black/95 via-black/70 to-transparent" />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent" />
              <div className="absolute inset-0 bg-gradient-to-r from-purple-900/30 via-transparent to-transparent" />
            </div>

            {/* Event Content Overlay */}
            <div className="relative z-10 h-full flex items-center">
              <div className="container mx-auto px-3 sm:px-4">
                <motion.div
                  initial={{ x: -30, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ delay: 0.3, duration: 0.6 }}
                  className="max-w-lg sm:max-w-xl"
                >
                  {/* Category Badge - Smaller on mobile */}
                  <motion.div
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.4 }}
                    className={`inline-block px-2 sm:px-3 py-1 rounded-full text-[10px] sm:text-xs font-semibold mb-2 sm:mb-3 bg-gradient-to-r ${getCategoryColor(currentEvent.category)} text-white`}
                  >
                    {currentEvent.category.toUpperCase()}
                  </motion.div>

                  {/* Event Title - Smaller on mobile */}
                  <motion.h3
                    initial={{ y: 30, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.5 }}
                    className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-azonix font-bold text-white mb-2 sm:mb-3 leading-tight"
                  >
                    {currentEvent.title}
                  </motion.h3>

                  {/* Event Details - Compact on mobile */}
                  <motion.div
                    initial={{ y: 30, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.6 }}
                    className="flex flex-wrap gap-2 sm:gap-3 mb-2 sm:mb-3 text-gray-300 text-xs sm:text-sm"
                  >
                    <span className="flex items-center gap-1">
                      <Calendar size={12} className="text-purple-400" />
                      {formatDate(currentEvent.date)}
                      {currentEvent.endDate && ` - ${formatDate(currentEvent.endDate)}`}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock size={12} className="text-purple-400" />
                      {currentEvent.time}
                    </span>
                  </motion.div>

                  <motion.div
                    initial={{ y: 30, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.7 }}
                    className="flex items-center gap-1.5 sm:gap-2 mb-3 sm:mb-4 text-gray-300 text-xs sm:text-sm"
                  >
                    <MapPin size={12} className="text-purple-400" />
                    <span className="truncate">{currentEvent.location}</span>
                  </motion.div>

                  {/* Description Preview - Shorter on mobile */}
                  <motion.p
                    initial={{ y: 30, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.8 }}
                    className="text-gray-200 text-xs sm:text-sm md:text-base font-inter leading-relaxed line-clamp-2 sm:line-clamp-3 mb-3 sm:mb-4"
                  >
                    {currentEvent.description}
                  </motion.p>

                  {/* CTA Button - Smaller on mobile */}
                  <motion.button
                    initial={{ y: 30, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.9 }}
                    className="inline-flex items-center px-4 sm:px-5 py-2 sm:py-2.5 bg-gradient-to-r from-purple-600 to-pink-600 text-white text-xs sm:text-sm font-semibold rounded-lg transition-all duration-300 hover:scale-105 hover:shadow-lg hover:shadow-purple-500/30"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedEvent(currentEvent);
                    }}
                  >
                    Learn More
                    <ExternalLink size={14} className="ml-1.5" />
                  </motion.button>
                </motion.div>
              </div>
            </div>
          </motion.div>
        </AnimatePresence>

        {/* Navigation Arrows - Smaller on mobile */}
        <button 
          onClick={(e) => { e.stopPropagation(); prevSlide(); }}
          className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 z-20 p-2 sm:p-3 bg-black/60 text-white rounded-full hover:bg-black/80 transition-all duration-300 hover:scale-110"
        >
          <ChevronLeft size={20} />
        </button>
        <button 
          onClick={(e) => { e.stopPropagation(); nextSlide(); }}
          className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 z-20 p-2 sm:p-3 bg-black/60 text-white rounded-full hover:bg-black/80 transition-all duration-300 hover:scale-110"
        >
          <ChevronRight size={20} />
        </button>

        {/* Dots Indicator */}
        <div className="absolute bottom-3 sm:bottom-4 left-1/2 -translate-x-1/2 z-20 flex gap-1.5 sm:gap-2">
          {events.map((_, index) => (
            <button
              key={index}
              onClick={(e) => { e.stopPropagation(); goToSlide(index); }}
              className={`rounded-full transition-all duration-300 ${
                index === currentIndex 
                  ? 'bg-purple-500 w-5 sm:w-6 h-2 sm:h-2.5' 
                  : 'bg-white/30 w-2 h-2 sm:w-2.5 sm:h-2.5 hover:bg-white/50'
              }`}
            />
          ))}
        </div>

        {/* Event Counter - Smaller on mobile */}
        <div className="absolute top-3 sm:top-4 right-3 sm:right-4 z-20 px-2 sm:px-3 py-1 bg-black/60 rounded-full text-white text-xs font-inter">
          {currentIndex + 1} / {events.length}
        </div>
      </div>

      {/* Event Detail Modal - Mobile optimized */}
      <AnimatePresence>
        {selectedEvent && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/90 backdrop-blur-sm z-50 flex items-center justify-center p-2"
            onClick={() => setSelectedEvent(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              className="bg-black rounded-xl sm:rounded-2xl max-w-lg w-full max-h-[80vh] overflow-hidden border border-purple-500/30 flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header with Image */}
              <div className="relative flex-shrink-0">
                <img 
                  src={selectedEvent.image} 
                  alt={selectedEvent.title} 
                  className="w-full h-28 sm:h-36 object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />
                <button 
                  onClick={() => setSelectedEvent(null)} 
                  className="absolute top-2 right-2 p-1.5 sm:p-2 bg-black/50 text-white rounded-full hover:bg-black/70 transition-all duration-300"
                  aria-label="Close modal"
                >
                  <X size={16} />
                </button>
                <div className="absolute bottom-2 left-2 right-2">
                  <span className={`inline-block px-2 sm:px-3 py-0.5 sm:py-1 rounded-full text-[10px] sm:text-xs font-semibold bg-gradient-to-r ${getCategoryColor(selectedEvent.category)} text-white mb-1`}>
                    {selectedEvent.category.toUpperCase()}
                  </span>
                  <h2 className="text-lg sm:text-xl md:text-2xl font-azonix font-bold text-white line-clamp-2">
                    {selectedEvent.title}
                  </h2>
                </div>
              </div>

              {/* Modal Content - Scrollable */}
              <div className="p-3 sm:p-4 overflow-y-auto flex-1">
                {/* Event Details - Compact grid */}
                <div className="grid grid-cols-2 gap-2 sm:gap-3 mb-4">
                  <div className="flex items-center gap-2 text-gray-300">
                    <div className="p-1.5 bg-purple-500/20 rounded-lg">
                      <Calendar size={14} className="text-purple-400" />
                    </div>
                    <div>
                      <p className="text-[10px] text-gray-500">Date</p>
                      <p className="text-xs font-medium">
                        {formatDate(selectedEvent.date)}
                        {selectedEvent.endDate && ` - ${formatDate(selectedEvent.endDate)}`}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-gray-300">
                    <div className="p-1.5 bg-purple-500/20 rounded-lg">
                      <Clock size={14} className="text-purple-400" />
                    </div>
                    <div>
                      <p className="text-[10px] text-gray-500">Time</p>
                      <p className="text-xs font-medium">{selectedEvent.time}</p>
                    </div>
                  </div>
                  <div className="col-span-2 flex items-center gap-2 text-gray-300">
                    <div className="p-1.5 bg-purple-500/20 rounded-lg">
                      <MapPin size={14} className="text-purple-400" />
                    </div>
                    <div>
                      <p className="text-[10px] text-gray-500">Location</p>
                      <p className="text-xs font-medium truncate">{selectedEvent.location}</p>
                    </div>
                  </div>
                </div>

                {/* Description */}
                <div className="mb-4">
                  <h3 className="text-sm font-azonix font-bold text-white mb-2">About This Event</h3>
                  <p className="text-gray-400 text-xs sm:text-sm leading-relaxed font-inter">
                    {selectedEvent.description}
                  </p>
                </div>

                {/* Action Button */}
                <div className="mt-2">
                  <a 
                    href="https://chat.whatsapp.com/example"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center w-full px-4 py-2.5 sm:py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white text-xs sm:text-sm font-semibold rounded-lg transition-all duration-300 hover:scale-105 hover:shadow-lg hover:shadow-purple-500/30"
                  >
                    Join WhatsApp Community
                    <ExternalLink size={14} className="ml-1.5" />
                  </a>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default CommunityEvents;
